import React, { useState } from "react";
import { useSelector } from "react-redux";
import Grid2 from "@mui/material/Grid2";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { HttpsCallable, httpsCallable } from "firebase/functions";

import AddPhoneIconBtn from "app/Components/Buttons/IconBtns/AddPhoneIconBtn";
import SaveIconBtn from "app/Components/Buttons/IconBtns/SaveIconBtn";
import CancelIconBtn from "app/Components/Buttons/IconBtns/CancelIconBtn";

import { firefuncs } from "config/firebase";
import { formatPhoneNumber } from "utils/helperFunctions";
import { RootState } from "store";

// Extended User type with phoneNumber field
interface UserWithPhone {
  uid: string;
  email: string | null;
  displayName?: string | null;
  photoURL?: string | null;
  emailVerified: boolean;
  phoneNumber?: string;
}

interface FormValues {
  displayName: string | null | undefined;
  textCode: string;
  phoneNumber: string;
  successMsg: string;
  responseMsg: string;
}

interface GenerateCodeParams {
  phoneNumber: string;
  userId: string;
}

interface GenerateCodeResponse {
  success: boolean;
  message?: string;
}

interface ValidateCodeParams {
  code: string;
}

interface ValidateCodeResponse {
  msg: string;
}

const UpdatePhoneNumber: React.FC = () => {
  const user = useSelector((state: RootState) => state.user.user) as UserWithPhone | null;
  
  const [loading, setLoading] = useState<boolean>(false);
  const [validating, setValidating] = useState<boolean>(false);
  const [showVerificationCodeField, setShowVerificationCodeField] = useState<boolean>(false);
  const [updatingNumber, setUpdatingNumber] = useState<boolean>(false);

  const [values, setValues] = useState<FormValues>({
    displayName: user?.displayName,
    textCode: "",
    phoneNumber: "",
    successMsg: "",
    responseMsg: "",
  });

  // Type-safe Firebase functions
  const generateCode: HttpsCallable<GenerateCodeParams, GenerateCodeResponse> = 
    httpsCallable(firefuncs, "generateCode");
  
  const validateCode: HttpsCallable<ValidateCodeParams, ValidateCodeResponse> = 
    httpsCallable(firefuncs, "validateCode");

  const sendVerificationText = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValues({ ...values, responseMsg: "" });
    setLoading(true);

    let numE164 = values.phoneNumber;

    if (numE164.length < 12) {
      setValues({ ...values, responseMsg: `Please use a valid phone number!` });
      setLoading(false);
      return;
    }

    numE164 = `+1${numE164.replace(/\D/g, "")}`;

    if (!user) {
      setValues({ ...values, responseMsg: "User not found" });
      setLoading(false);
      return;
    }

    try {
      const res = await generateCode({
        phoneNumber: numE164,
        userId: user.uid,
      });

      console.log("verification text sent successfully", res);
      setShowVerificationCodeField(true);
      setValues({
        ...values,
        responseMsg: `Validation Code sent to ${values.phoneNumber}`,
      });
    } catch (err) {
      console.error("Error sending verification code", err);
      const errorMessage = err instanceof Error ? err.message : "An error occurred";
      setValues({ ...values, responseMsg: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const { name, value } = e.target;
    const val = value.replace(/\D/g, "");
    let formattedValue = "";

    if (val) {
      formattedValue = formatPhoneNumber(val);
    }

    setValues({ ...values, [name]: formattedValue });
  };

  const handleValidateCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setValidating(true);

    try {
      const result = await validateCode({ code: values.textCode });
      setValues({ ...values, successMsg: result.data.msg });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Validation failed";
      setValues({ ...values, responseMsg: errorMessage });
    } finally {
      setValidating(false);
      setUpdatingNumber(false);
    }
  };

  const renderUpdatePhoneNumberOptions = (): React.ReactElement | null => {
    if (!user) return null;

    let tip = " Phone Number";
    let verb = "Add";
    
    if (user.phoneNumber) {
      verb = "Update";
    }
    tip = verb + tip;

    if (!updatingNumber && !user.phoneNumber) {
      return (
        <span>
          None on File
          <AddPhoneIconBtn 
            action={() => setUpdatingNumber(true)} 
            tip={tip} 
          />
        </span>
      );
    }
    
    if (!updatingNumber && user.phoneNumber) {
      return (
        <span>
          +1 {formatPhoneNumber(user.phoneNumber)}
          <AddPhoneIconBtn 
            action={() => setUpdatingNumber(true)} 
            tip={tip} 
          />
        </span>
      );
    }

    return null;
  };

  const renderInputPhoneNumberField = () => {
    if (updatingNumber && !loading) {
      return (
        <form className="form-styles" onSubmit={sendVerificationText}>
          <Grid2 
            container 
            direction="row" 
            justifyContent="center" 
            alignItems="center"
            spacing={1}
          >
            <Grid2>+1</Grid2>
            <Grid2>
              <input
                value={values.phoneNumber}
                onChange={handlePhoneInputChange}
                type="tel"
                name="phoneNumber"
                className="form-control"
                id="phoneNumber"
                placeholder="###-###-####"
                autoComplete="tel"
              />
            </Grid2>
          </Grid2>
          <SaveIconBtn action={(e) => sendVerificationText(e as any)} />
          <CancelIconBtn action={() => setUpdatingNumber(false)} />
          {values.responseMsg && (
            <div style={{ color: "red", marginTop: "10px" }}>
              {values.responseMsg}
            </div>
          )}
        </form>
      );
    }
    
    if (updatingNumber && loading) {
      return <CircularProgress />;
    }

    return null;
  };

  const renderVerificationCodeField = (): React.ReactElement | null => {
    if (!showVerificationCodeField) {
      return null;
    }

    return (
      <div>
        <form onSubmit={handleValidateCode} className="form-styles">
          <label htmlFor="TextCode">Input Verification Code:</label>
          <Grid2
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={1}
          >
            <Grid2>
              <input
                value={values.textCode}
                onChange={(e) => {
                  setValues({ ...values, textCode: e.target.value });
                }}
                name="textCode"
                className="form-control"
                id="TextCode"
                placeholder="Enter code"
                autoComplete="off"
              />
            </Grid2>
          </Grid2>
        </form>
        
        {validating ? (
          <CircularProgress color="secondary" />
        ) : (
          <>
            <Button
              onClick={handleValidateCode}
              variant="contained"
              color="secondary"
              style={{ marginTop: "10px" }}
            >
              Submit
            </Button>
            {values.successMsg && (
              <div style={{ color: "green", marginTop: "10px" }}>
                {values.successMsg}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {renderUpdatePhoneNumberOptions()}
      {renderInputPhoneNumberField()}
      {renderVerificationCodeField()}
    </>
  );
};

export default UpdatePhoneNumber;